import Papa from "papaparse";
import { fetchCsvTextFromStorage } from "./mbStorageUtils";
import { parseLineStringToPath } from "./mbChatPureUtils";

const distanceSquared = (a, b) => (a.lat - b.lat) ** 2 + (a.lng - b.lng) ** 2;

const orientPath = (path, start, end) => {
  if (!start || !end || path.length < 2) return path;
  const first = path[0];
  const last = path[path.length - 1];
  const forward = distanceSquared(first, start) + distanceSquared(last, end);
  const backward = distanceSquared(first, end) + distanceSquared(last, start);
  return backward < forward ? [...path].reverse() : path;
};

export async function resolveTemporalGeometry(segments, network = {}) {
  const links = new Map();
  const nodes = new Map();
  const endpoints = new Map();
  segments.forEach((segment) => {
    if (segment.entityType === "link" && segment.path) links.set(segment.entityId, segment.path);
    if (segment.entityType === "node" && segment.position) nodes.set(segment.entityId, segment.position);
  });

  const resultId = String(network.resultId || "").replace(/^\/+|\/+$/g, "");
  if (resultId) {
    const missingLinks = new Set(segments.filter((segment) => segment.entityType === "link" && !links.has(segment.entityId)).map((segment) => segment.entityId));
    const missingNodes = new Set(segments.filter((segment) => segment.entityType === "node" && !nodes.has(segment.entityId)).map((segment) => segment.entityId));
    const [linksCsv, nodesCsv] = await Promise.all([
      missingLinks.size ? fetchCsvTextFromStorage(`${resultId}/inputs/${network.linksFileName || "berkeley_links.csv"}`) : null,
      (missingNodes.size || missingLinks.size) ? fetchCsvTextFromStorage(`${resultId}/inputs/${network.nodesFileName || "berkeley_nodes.csv"}`) : null,
    ]);
    if (linksCsv) Papa.parse(linksCsv, { header: true, skipEmptyLines: true }).data.forEach((row) => {
      const id = String(row.link_id || row.id || "");
      if (!missingLinks.has(id)) return;
      const path = parseLineStringToPath(row.geometry || row.geom || row.wkt);
      if (path) {
        links.set(id, path);
        endpoints.set(id, {
          startId: String(row.start_node_id || row.from_node_id || row.start_node || row.from_node || ""),
          endId: String(row.end_node_id || row.to_node_id || row.end_node || row.to_node || ""),
        });
      }
    });
    const networkNodes = new Map();
    if (nodesCsv) Papa.parse(nodesCsv, { header: true, skipEmptyLines: true }).data.forEach((row) => {
      const id = String(row.node_id || row.nodeid || row.id || "");
      const lat = Number(row.lat ?? row.latitude);
      const lng = Number(row.lon ?? row.lng ?? row.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const position = { lat, lng };
      networkNodes.set(id, position);
      if (missingNodes.has(id)) nodes.set(id, position);
    });
    endpoints.forEach(({ startId, endId }, id) => {
      links.set(id, orientPath(links.get(id), networkNodes.get(startId), networkNodes.get(endId)));
    });
  }

  return {
    links: [...links].map(([id, path]) => ({ id, path })),
    nodes: [...nodes].map(([id, position]) => ({ id, ...position })),
    missingGeometry: segments.filter((segment) => segment.entityType === "link" ? !links.has(segment.entityId) : !nodes.has(segment.entityId)).length,
  };
}
